import React, { useState, useEffect, useContext, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
  useTheme,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddEmployeeLeaveModal from './AddEmployeeLeaveModal';
import ViewLeaveModal from './ViewLeaveModal';
import EditEmployeeLeaveModal from './EditEmployeeLeaveModal';
import { FetchContext } from 'src/context/FetchContext';
import { AuthContext } from 'src/context/AuthContext';
import { API_ROUTES } from 'src/services/constant';

const { PRIVATE_ROUTE } = API_ROUTES;

const styles = {
  calendar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    width: '100%',
    margin: 'auto',
    gap: '3px',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    fontWeight: 'bold',
    textAlign: 'center',
    padding: '8px 4px',
    backgroundColor: '#eee',
    fontSize: 'clamp(10px, 1.5vw, 15px)',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  cell: {
    minHeight: '100px',
    border: '1px solid #ccc',
    textAlign: 'center',
    backgroundColor: '#fafafa',
    position: 'relative',
    padding: '0 0 1em',
    overflow: 'hidden',
    minWidth: 0,
  },
  empty: {
    backgroundColor: 'transparent',
    border: 'none',
  },
  select: {
    padding: '6px 10px',
    fontSize: '14px',
  },
};

const style = {
  width: '100%',
  height: 'calc(100vh - 92px)',
  p: { xs: 1, sm: 2, md: 3 },
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const formatDate = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export default function EmployeeLeavePlanner() {
  const { authAxios } = useContext(FetchContext);
  const { state: authState } = useContext(AuthContext);
  const currentUser = authState.user;
  const isAdminOrManager = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  const theme = useTheme();
  const isMobilePortrait = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileLandscape = useMediaQuery('(max-height: 500px) and (orientation: landscape)');
  const isMobile = isMobilePortrait || isMobileLandscape;

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [addModal, setAddModal] = useState({ open: false, date: '' });
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [leaveDates, setLeaveDates] = useState([]);
  const [viewModal, setViewModal] = useState({ open: false, date: '', records: [] });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, recordId: null, record: null, clickedDate: null, action: 'delete' });
  const [editModal, setEditModal] = useState({ open: false, record: null });

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const blanks = Array(firstDay).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const fetchLeaves = useCallback(async () => {
    try {
      const { data } = await authAxios.get(
        `${PRIVATE_ROUTE.GET_EMPLOYEE_LEAVES}?year=${year}&month=${month + 1}`
      );
      const records = data.data || [];
      const filtered = isAdminOrManager || !currentUser
        ? records
        : records.filter((l) => {
            const empId = l.employee_id?._id
              ? String(l.employee_id._id)
              : String(l.employee_id);
            return empId === String(currentUser._id);
          });
      setLeaveRecords(filtered);
      const mo = month + 1;
      const lastDay = new Date(year, mo, 0).getDate();
      const monthStart = `${year}-${String(mo).padStart(2, '0')}-01`;
      const monthEnd = `${year}-${String(mo).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const allDates = [];
      filtered.forEach((l) => {
        const from = l.date_from?.substring(0, 10);
        if (!from) return;
        const to = l.date_to?.substring(0, 10);
        if (!to || from === to) { allDates.push(from); return; }
        const clampedStart = from > monthStart ? from : monthStart;
        const clampedEnd = to < monthEnd ? to : monthEnd;
        let cur = new Date(`${clampedStart}T00:00:00`);
        const end = new Date(`${clampedEnd}T00:00:00`);
        while (cur <= end) {
          allDates.push(cur.toISOString().substring(0, 10));
          cur.setDate(cur.getDate() + 1);
        }
      });
      setLeaveDates(allDates);
    } catch (e) {
      console.error('Failed to fetch leaves', e);
    }
  }, [authAxios, isAdminOrManager, currentUser, year, month]);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves, year, month]);

  const getDeleteAction = (rec, dateKey) => {
    const from = rec.date_from?.substring(0, 10);
    const to = rec.date_to?.substring(0, 10);
    if (!to || from === to) return 'delete';
    if (dateKey === from) return 'update_start';
    if (dateKey === to) return 'update_end';
    return 'split';
  };

  const closeConfirmDelete = () =>
    setConfirmDelete({ open: false, recordId: null, record: null, clickedDate: null, action: 'delete' });

  const handleDeleteConfirmed = async () => {
    const { recordId, record, clickedDate, action } = confirmDelete;
    closeConfirmDelete();
    if (!recordId) return;
    try {
      if (action === 'update_start') {
        const newFrom = new Date(`${clickedDate}T00:00:00`);
        newFrom.setDate(newFrom.getDate() + 1);
        const newFromStr = newFrom.toISOString().substring(0, 10);
        const to = record.date_to?.substring(0, 10);
        if (!to || newFromStr > to) {
          await authAxios.post(`${PRIVATE_ROUTE.DELETE_EMPLOYEE_LEAVE}${recordId}`);
        } else {
          await authAxios.post(`${PRIVATE_ROUTE.EDIT_EMPLOYEE_LEAVE}${recordId}`, {
            employee_id: record.employee_id?._id ?? record.employee_id,
            job_id: record.job_id?._id ?? record.job_id,
            task_id: record.task_id?._id ?? record.task_id,
            time_from: record.time_from || null,
            time_to: record.time_to || null,
            cost_item: record.cost_item || null,
            description: record.description || null,
            date_from: newFromStr,
            date_to: newFromStr === to ? null : to,
          });
        }
      } else if (action === 'update_end') {
        const newTo = new Date(`${clickedDate}T00:00:00`);
        newTo.setDate(newTo.getDate() - 1);
        const newToStr = newTo.toISOString().substring(0, 10);
        const from = record.date_from?.substring(0, 10);
        if (!from || newToStr < from) {
          await authAxios.post(`${PRIVATE_ROUTE.DELETE_EMPLOYEE_LEAVE}${recordId}`);
        } else {
          await authAxios.post(`${PRIVATE_ROUTE.EDIT_EMPLOYEE_LEAVE}${recordId}`, {
            employee_id: record.employee_id?._id ?? record.employee_id,
            job_id: record.job_id?._id ?? record.job_id,
            task_id: record.task_id?._id ?? record.task_id,
            time_from: record.time_from || null,
            time_to: record.time_to || null,
            cost_item: record.cost_item || null,
            description: record.description || null,
            date_from: from,
            date_to: newToStr === from ? null : newToStr,
          });
        }
      } else if (action === 'split') {
        const from = record.date_from?.substring(0, 10);
        const to = record.date_to?.substring(0, 10);
        const firstHalfEnd = new Date(`${clickedDate}T00:00:00`);
        firstHalfEnd.setDate(firstHalfEnd.getDate() - 1);
        const firstHalfEndStr = firstHalfEnd.toISOString().substring(0, 10);
        const secondHalfStart = new Date(`${clickedDate}T00:00:00`);
        secondHalfStart.setDate(secondHalfStart.getDate() + 1);
        const secondHalfStartStr = secondHalfStart.toISOString().substring(0, 10);
        const commonFields = {
          employee_id: record.employee_id?._id ?? record.employee_id,
          job_id: record.job_id?._id ?? record.job_id,
          task_id: record.task_id?._id ?? record.task_id,
          time_from: record.time_from || null,
          time_to: record.time_to || null,
          cost_item: record.cost_item || null,
          description: record.description || null,
        };
        await authAxios.post(`${PRIVATE_ROUTE.EDIT_EMPLOYEE_LEAVE}${recordId}`, {
          ...commonFields,
          date_from: from,
          date_to: firstHalfEndStr === from ? null : firstHalfEndStr,
        });
        await authAxios.post(`${PRIVATE_ROUTE.CREATE_EMPLOYEE_LEAVE}`, {
          ...commonFields,
          date_from: secondHalfStartStr,
          date_to: secondHalfStartStr === to ? null : to,
        });
      } else {
        await authAxios.post(`${PRIVATE_ROUTE.DELETE_EMPLOYEE_LEAVE}${recordId}`);
      }
      fetchLeaves();
    } catch (e) {
      console.error('Failed to update/delete leave', e);
    }
  };

  const handleSubmit = () => {
    setYear(selectedYear);
    setMonth(selectedMonth);
  };

  return (
    <>
      <AddEmployeeLeaveModal
        open={addModal.open}
        onClose={() => setAddModal({ open: false, date: '' })}
        defaultDate={addModal.date}
        onSuccess={fetchLeaves}
      />
      <ViewLeaveModal
        open={viewModal.open}
        onClose={() => setViewModal({ open: false, date: '', records: [] })}
        date={viewModal.date}
        records={viewModal.records}
        isAdminOrManager={isAdminOrManager}
        onEdit={(rec) => {
          setViewModal({ open: false, date: '', records: [] });
          setEditModal({ open: true, record: rec });
        }}
        onDelete={(rec) => {
          const clickedDate = viewModal.date;
          const action = getDeleteAction(rec, clickedDate);
          setViewModal({ open: false, date: '', records: [] });
          setConfirmDelete({ open: true, recordId: rec._id, record: rec, clickedDate, action });
        }}
      />
      <EditEmployeeLeaveModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, record: null })}
        record={editModal.record}
        onSuccess={fetchLeaves}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmDelete.open}
        onClose={closeConfirmDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WarningAmberIcon sx={{ color: '#EF4444' }} />
          {confirmDelete.action === 'delete'
            ? 'Delete Leave Entry'
            : confirmDelete.action === 'split'
            ? 'Split Leave Range'
            : 'Shorten Leave Range'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.9rem', color: '#444' }}>
            {confirmDelete.action === 'update_start'
              ? `This will remove ${confirmDelete.clickedDate} from the start of the leave range. The leave will begin one day later.`
              : confirmDelete.action === 'update_end'
              ? `This will remove ${confirmDelete.clickedDate} from the end of the leave range. The leave will end one day earlier.`
              : confirmDelete.action === 'split'
              ? `This will split the leave range at ${confirmDelete.clickedDate}. Two separate leave entries will be created — one before and one after this date.`
              : 'Are you sure you want to delete this leave entry? This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={closeConfirmDelete}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirmed}
          >
            {confirmDelete.action === 'delete' ? 'Delete' : confirmDelete.action === 'split' ? 'Split' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
      <Box id="employee-leave-planner" sx={style}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
          mb: 2,
          padding: '0.5em',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2a2a2a' }}>
          Employee Leave Planner
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <select
              style={styles.select}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {months.map((m, index) => (
                <option key={m} value={index}>{m}</option>
              ))}
            </select>
          </Box>
          <select
            style={styles.select}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>
        </Box>
      </Box>

      {/* Calendar Grid */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        <div style={styles.calendar}>
          {/* Day Headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} style={styles.header}>{day}</div>
          ))}

          {/* Empty cells */}
          {blanks.map((_, index) => (
            <div
              key={`blank-${index}`}
              style={{
                ...styles.cell,
                ...styles.empty,
                minHeight: isMobile ? '80px' : '130px',
                height: isMobile ? '80px' : '130px',
              }}
            />
          ))}

          {/* Day cells */}
          {days.map((day) => {
            const dateKey = formatDate(year, month, day);
            const isToday =
              day === today.getDate() &&
              month === today.getMonth() &&
              year === today.getFullYear();

            return (
              <div
                key={dateKey}
                style={{
                  ...styles.cell,
                  backgroundColor: isToday ? '#fff5f5' : '#fafafa',
                  height: isMobile ? '80px' : '130px',
                  minHeight: isMobile ? '80px' : '130px',
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '3px 5px 2px',
                    gap: '2px',
                  }}
                >
                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                    <Tooltip title="Add" arrow placement="top">
                      <button
                        type="button"
                        onClick={() => setAddModal({ open: true, date: dateKey })}
                        style={{
                          fontSize: 'clamp(7px, 0.7vw, 9px)',
                          fontWeight: 700,
                          color: '#fff',
                          background: '#3DADB5',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '1px 4px',
                          cursor: 'pointer',
                          lineHeight: 1.4,
                        }}
                      >
                        +
                      </button>
                    </Tooltip>
                    {!isAdminOrManager && leaveDates.includes(dateKey) && (
                      <>
                        <Tooltip title="Edit" arrow placement="top">
                          <button
                            type="button"
                            onClick={() => {
                              const rec = leaveRecords.find((l) => {
                                const from = l.date_from?.substring(0, 10);
                                if (!from) return false;
                                const to = l.date_to?.substring(0, 10);
                                if (!to) return from === dateKey;
                                return dateKey >= from && dateKey <= to;
                              });
                              if (rec) setEditModal({ open: true, record: rec });
                            }}
                            style={{
                              fontSize: 'clamp(7px, 0.7vw, 9px)',
                              fontWeight: 700,
                              color: '#fff',
                              background: '#F59E0B',
                              border: 'none',
                              borderRadius: '3px',
                              padding: '1px 4px',
                              cursor: 'pointer',
                              lineHeight: 1.4,
                            }}
                          >
                            ✎
                          </button>
                        </Tooltip>
                        <Tooltip title="Delete" arrow placement="top">
                          <button
                            type="button"
                            onClick={() => {
                              const rec = leaveRecords.find((l) => {
                                const from = l.date_from?.substring(0, 10);
                                if (!from) return false;
                                const to = l.date_to?.substring(0, 10);
                                if (!to) return from === dateKey;
                                return dateKey >= from && dateKey <= to;
                              });
                              if (rec) {
                                const action = getDeleteAction(rec, dateKey);
                                setConfirmDelete({ open: true, recordId: rec._id, record: rec, clickedDate: dateKey, action });
                              }
                            }}
                            style={{
                              fontSize: 'clamp(7px, 0.7vw, 9px)',
                              fontWeight: 700,
                              color: '#fff',
                              background: '#EF4444',
                              border: 'none',
                              borderRadius: '3px',
                              padding: '1px 4px',
                              cursor: 'pointer',
                              lineHeight: 1.4,
                            }}
                          >
                            ✕
                          </button>
                        </Tooltip>
                      </>
                    )}
                  </div>

                  {/* Date number */}
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: isToday ? '#d32f2f' : 'transparent',
                      color: isToday ? '#fff' : '#333',
                      fontWeight: isToday ? 'bold' : '500',
                      fontSize: 'clamp(10px, 1.1vw, 13px)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      flexShrink: 0,
                      marginLeft: 'auto',
                    }}
                  >
                    {day}
                  </span>
                </div>

                {/* View button — centered in cell body */}
                {leaveDates.includes(dateKey) && (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setViewModal({
                          open: true,
                          date: dateKey,
                          records: leaveRecords.filter((l) => {
                            const from = l.date_from?.substring(0, 10);
                            if (!from) return false;
                            const to = l.date_to?.substring(0, 10);
                            if (!to) return from === dateKey;
                            return dateKey >= from && dateKey <= to;
                          }),
                        })
                      }
                      style={{
                        fontSize: 'clamp(8px, 0.85vw, 11px)',
                        fontWeight: '700',
                        color: '#fff',
                        background: '#3DADB5',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '3px 5px',
                        cursor: 'pointer',
                        width: '100%',
                      }}
                    >
                      View
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Box>
    </>
  );
}
